import React from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { useAssessment, useMissionRoleApplicant } from "hooks";
import { MessagesError } from "components/Messages";
import { Grid } from "components/Containers";
import {
  ApplicationDisplayBoxSection,
  ApplicationMatchingTags,
  ApplicationQuestionnaireResponses,
  ApplicationRateForClient,
  RATE_DISPLAYS,
} from "components/Missions/Applicants";
import Card from "components/Card";
import { ProfileAiSummary } from "components/Profile";
import { Editor } from "components/Form";

/**
 * ApplicationSummary
 *
 * @param {Objects} application
 */
const ApplicationSummary = ({ application }) => {
  if (!application) return null;

  const { loading, data, error } = useMissionRoleApplicant({ id: application?.id });

  const { data: assessment, loading: assessmentLoading } = useAssessment({
    id: data?.missionRole?.assessmentId,
    resourceId: data?.profile?.id,
  });

  return (
    <>
      {error ? (
        <MessagesError />
      ) : (
        <Grid gap="1rem">
          <Grid.col start={1} end={13}>
            <Card>
              <ProfileAiSummary
                profileId={data?.profile?.uuid}
                loading={loading}
                msgId="Missions.Applicants.ApplicantDetailsModal.LabelAISummary"
              />
            </Card>
          </Grid.col>
          {data?.clientNotes && (
            <Grid.col start={1} end={13}>
              <Card>
                <ApplicationDisplayBoxSection
                  msgId="Missions.Applicants.ApplicantDetailsModal.LabelNotes"
                  renderer={ClientNotes}
                  rendererProps={{ value: data?.clientNotesJson }}
                />
              </Card>
            </Grid.col>
          )}
          <Grid.col start={1} end={13}>
            <Card>
              <ApplicationDisplayBoxSection
                msgId="Missions.MissionCandidateList.RateScore"
                data={<ApplicationRateForClient application={application} display={RATE_DISPLAYS.tag} />}
              />
            </Card>
          </Grid.col>
          <Grid.col start={1} end={13}>
            <Card>
              <ApplicationDisplayBoxSection
                msgId="Missions.MissionCandidateList.SkillsMatch"
                data={<ApplicationMatchingTags loading={loading} data={data} flatView />}
              />
            </Card>
          </Grid.col>
          <Grid.col start={1} end={13}>
            <Card>
              <ApplicationDisplayBoxSection
                msgId="Global.QuestionsAnswers"
                data={
                  <ApplicationQuestionnaireResponses
                    loading={loading || assessmentLoading}
                    data={data}
                    assessment={assessment}
                  />
                }
              />
            </Card>
          </Grid.col>
        </Grid>
      )}
    </>
  );
};

const styles = {
  body: css`
    margin: 0;
    padding: 0;
    border: 0;
    box-shadow: none;
  `,
};

ApplicationSummary.propTypes = {
  application: PropTypes.object.isRequired,
};

/**
 * EditorComponent for rendering client notes
 */
const ClientNotes = ({ value }) => <Editor value={value} css={styles.body} readOnly={true} />;

ClientNotes.propTypes = {
  value: PropTypes.object,
};

export default ApplicationSummary;
